import { useEffect, useState } from "react";
import { Modal } from "@components/ui/Modal";
import { Button } from "@components/ui/Button";
import { orderService } from "../../../services/orderService";
import type { Order } from "../../../types/order";

interface OrderRefundDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  order: Order | null;
}

const HIGH_VALUE_THRESHOLD_USD = 500;

const formatCurrency = (value: number, currency: string | null): string => {
  return `${value.toFixed(2)} ${currency || "USD"}`;
};

export function OrderRefundDialog({
  isOpen,
  onClose,
  onSuccess,
  order,
}: OrderRefundDialogProps) {
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError(null);
      setConfirmText("");
      setIsLoading(false);
    }
  }, [isOpen, order?._id]);

  if (!isOpen || !order) return null;

  const isHighValue = order.totalUSD >= HIGH_VALUE_THRESHOLD_USD;
  const requiresTypedConfirmation = isHighValue;
  const confirmationOk = !requiresTypedConfirmation || confirmText.trim() === "REFUND";

  const handleSubmit = async () => {
    if (!confirmationOk) return;
    setIsLoading(true);
    setError(null);
    try {
      await orderService.refund(order._id, reason.trim());
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message ||
        "Failed to refund order. Please try again.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Refund Order"
      className="max-w-md"
    >
      <div className="space-y-4">
        <section className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500 mb-1">Order</p>
          <p className="font-mono text-xs font-medium text-gray-900 break-all">
            {order._id}
          </p>
          <p className="font-semibold text-gray-900 mt-2">
            Total: {formatCurrency(order.totalUSD, order.currency)}
          </p>
          {order.coupon && (
            <p className="text-xs text-gray-500 mt-1">
              Coupon applied: <strong>{order.coupon.code}</strong> (−
              {order.coupon.discountUSD.toFixed(2)})
            </p>
          )}
        </section>

        <div>
          <label
            htmlFor="order-refund-reason"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Refund reason
          </label>
          <textarea
            id="order-refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Optional admin note explaining the refund"
            maxLength={500}
          />
        </div>

        {requiresTypedConfirmation && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-medium text-amber-800 mb-2">
              This is a high-value order (&ge; $
              {HIGH_VALUE_THRESHOLD_USD.toFixed(0)}). To confirm, type{" "}
              <strong>REFUND</strong> below.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type REFUND to enable the button"
              className="w-full px-3 py-2 border border-amber-300 rounded-md text-sm uppercase tracking-wider focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              aria-label="Type REFUND to confirm"
            />
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-6 border-t border-gray-100 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            onClick={handleSubmit}
            disabled={isLoading || !confirmationOk}
          >
            {isLoading ? "Processing..." : "Refund Order"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
